/**
 * FIT Hotspot Scroll Showcase Web Component
 * Manages scroll-driven opacity transitions, sticky visual pinning,
 * and bi-directional hotspot interactions on Desktop and Mobile.
 */
class FitHotspotScroll extends HTMLElement {
  constructor() {
    super();
    this.pins = [];
    this.desktopItems = [];
    this.mobileCards = [];
    this.mobileTrack = null;
    this.currentActiveIndex = -1;
    this.desktopObserver = null;
    this.mobileObserver = null;
    this.isClickScrolling = false;
  }

  connectedCallback() {
    this.pins = Array.from(this.querySelectorAll('.fit-hotspot-scroll__pin'));
    this.desktopItems = Array.from(this.querySelectorAll('.fit-hotspot-scroll__item'));
    this.mobileCards = Array.from(this.querySelectorAll('.fit-hotspot-scroll__mobile-card'));
    this.mobileTrack = this.querySelector('.fit-hotspot-scroll__mobile-track');

    this.bindEvents();
    this.initDesktopScrollSpy();
    this.initMobileCarousel();
    this.initReadMore();
    this.initThemeEditorSupport();

    // Default activate first state
    if (this.desktopItems.length > 0 && window.innerWidth >= 990) {
      this.setActiveIndex(0, false);
    } else if (this.mobileCards.length > 0) {
      this.setActiveIndex(1, false);
    }
  }

  disconnectedCallback() {
    if (this.desktopObserver) this.desktopObserver.disconnect();
    if (this.mobileObserver) this.mobileObserver.disconnect();
  }

  bindEvents() {
    // Pin click interactions
    this.pins.forEach((pin) => {
      pin.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(pin.dataset.hotspotIndex, 10);
        if (isNaN(index)) return;

        if (window.innerWidth >= 990) {
          this.scrollToDesktopStep(index);
        } else {
          this.scrollToMobileCard(index);
        }
      });
    });

    // Mobile card clicks
    this.mobileCards.forEach((card) => {
      card.addEventListener('click', () => {
        const index = parseInt(card.dataset.mobileIndex, 10);
        if (!isNaN(index)) {
          this.scrollToMobileCard(index);
        }
      });
    });

    // Window resize handler for adjusting states
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 990) {
          if (this.currentActiveIndex === -1) this.setActiveIndex(0, false);
        } else {
          if (this.currentActiveIndex <= 0) this.setActiveIndex(1, false);
        }
      }, 150);
    });
  }

  /**
   * IntersectionObserver for Desktop scroll-driven fade storytelling
   */
  initDesktopScrollSpy() {
    if (!('IntersectionObserver' in window) || this.desktopItems.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: '-35% 0px -35% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1.0]
    };

    this.desktopObserver = new IntersectionObserver((entries) => {
      if (this.isClickScrolling) return;
      if (window.innerWidth < 990) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.stepIndex, 10);
          if (!isNaN(index)) {
            this.setActiveIndex(index, false);
          }
        }
      });
    }, observerOptions);

    this.desktopItems.forEach((item) => this.desktopObserver.observe(item));

    // Also track items passed above to translate upwards
    window.addEventListener('scroll', () => {
      if (window.innerWidth < 990) return;
      const vhCenter = window.innerHeight * 0.45;

      this.desktopItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.bottom < vhCenter) {
          item.classList.add('is-passed');
        } else {
          item.classList.remove('is-passed');
        }
      });
    }, { passive: true });
  }

  /**
   * Mobile Horizontal Carousel Tracking
   */
  initMobileCarousel() {
    if (!this.mobileTrack || this.mobileCards.length === 0) return;

    if ('IntersectionObserver' in window) {
      const mobileOptions = {
        root: this.mobileTrack,
        threshold: 0.65
      };

      this.mobileObserver = new IntersectionObserver((entries) => {
        if (window.innerWidth >= 990) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.mobileIndex, 10);
            if (!isNaN(index)) {
              this.setActiveIndex(index, true);
            }
          }
        });
      }, mobileOptions);

      this.mobileCards.forEach((card) => this.mobileObserver.observe(card));
    } else {
      // Fallback for browsers without IntersectionObserver
      this.mobileTrack.addEventListener('scroll', () => {
        if (window.innerWidth >= 990) return;
        const trackCenter = this.mobileTrack.scrollLeft + (this.mobileTrack.offsetWidth / 2);

        let closestCard = null;
        let minDistance = Infinity;

        this.mobileCards.forEach((card) => {
          const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
          const dist = Math.abs(trackCenter - cardCenter);
          if (dist < minDistance) {
            minDistance = dist;
            closestCard = card;
          }
        });

        if (closestCard) {
          const index = parseInt(closestCard.dataset.mobileIndex, 10);
          if (!isNaN(index)) this.setActiveIndex(index, true);
        }
      }, { passive: true });
    }
  }

  /**
   * Set active index and sync desktop items, mobile cards, and hotspot pins
   * @param {number} index - 0 for intro, 1..N for hotspot features
   * @param {boolean} isMobile - whether triggered from mobile
   */
  setActiveIndex(index, isMobile = false) {
    if (this.currentActiveIndex === index) return;
    this.currentActiveIndex = index;

    // 1. Update Desktop Items
    this.desktopItems.forEach((item) => {
      const itemIndex = parseInt(item.dataset.stepIndex, 10);
      if (itemIndex === index) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });

    // 2. Update Hotspot Pins (index 0 is intro, pins are 1-based)
    this.pins.forEach((pin) => {
      const pinIndex = parseInt(pin.dataset.hotspotIndex, 10);
      if (pinIndex === index) {
        pin.classList.add('is-active');
        pin.setAttribute('aria-expanded', 'true');
      } else {
        pin.classList.remove('is-active');
        pin.setAttribute('aria-expanded', 'false');
      }
    });

    // 3. Update Mobile Cards
    this.mobileCards.forEach((card) => {
      const cardIndex = parseInt(card.dataset.mobileIndex, 10);
      if (cardIndex === index) {
        card.classList.add('is-active');
      } else {
        card.classList.remove('is-active');
      }
    });
  }

  /**
   * Scroll smoothly to the desktop step
   */
  scrollToDesktopStep(index) {
    const targetItem = this.desktopItems.find(
      (item) => parseInt(item.dataset.stepIndex, 10) === index
    );
    if (!targetItem) return;

    this.isClickScrolling = true;
    this.setActiveIndex(index, false);

    const targetRect = targetItem.getBoundingClientRect();
    const targetTop = window.scrollY + targetRect.top - (window.innerHeight / 2) + (targetRect.height / 2);

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth'
    });

    setTimeout(() => {
      this.isClickScrolling = false;
    }, 800);
  }

  /**
   * Scroll smoothly to the mobile card
   */
  scrollToMobileCard(index) {
    const targetCard = this.mobileCards.find(
      (card) => parseInt(card.dataset.mobileIndex, 10) === index
    );
    if (!targetCard || !this.mobileTrack) return;

    this.setActiveIndex(index, true);

    const trackPaddingLeft = parseFloat(getComputedStyle(this.mobileTrack).paddingLeft) || 24;
    const targetScrollLeft = targetCard.offsetLeft - trackPaddingLeft;

    this.mobileTrack.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: 'smooth'
    });
  }

  /**
   * Automatic 4-line detection and Read More / Read Less toggle
   */
  initReadMore() {
    const descWraps = Array.from(this.querySelectorAll('.fit-hotspot-scroll__desc-wrap'));
    if (!descWraps.length) return;

    const checkOverflow = () => {
      descWraps.forEach((wrap) => {
        const textEl = wrap.querySelector('.fit-hotspot-scroll__clamp-4');
        const readMoreBtn = wrap.querySelector('.fit-hotspot-scroll__read-more');
        if (!textEl || !readMoreBtn) return;

        // If user already expanded this block, keep the button active
        if (wrap.classList.contains('is-expanded')) return;

        // Compare un-clamped scrollHeight with clamped clientHeight (with 2px threshold)
        const isOverflowing = textEl.scrollHeight > textEl.clientHeight + 2;
        if (isOverflowing) {
          readMoreBtn.style.display = 'inline-flex';
        } else {
          readMoreBtn.style.display = 'none';
        }
      });
    };

    // Attach click listeners to read more buttons
    descWraps.forEach((wrap) => {
      const readMoreBtn = wrap.querySelector('.fit-hotspot-scroll__read-more');
      if (!readMoreBtn) return;

      readMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isExpanded = wrap.classList.toggle('is-expanded');
        readMoreBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

        const textSpan = readMoreBtn.querySelector('.fit-hotspot-scroll__read-more-text');
        const readMoreLabel = readMoreBtn.dataset.readMore || 'Read more';
        const readLessLabel = readMoreBtn.dataset.readLess || 'Read less';

        if (textSpan) {
          textSpan.textContent = isExpanded ? readLessLabel : readMoreLabel;
        }
      });
    });

    // Check overflow upon initialization, font load, and window resize
    if ('fonts' in document) {
      document.fonts.ready.then(checkOverflow);
    }
    requestAnimationFrame(checkOverflow);
    setTimeout(checkOverflow, 150);
    setTimeout(checkOverflow, 600);

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkOverflow, 120);
    });
  }

  /**
   * Shopify Theme Editor support
   */
  initThemeEditorSupport() {
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const blockId = event.detail.blockId;
      const targetPin = this.pins.find((p) => p.dataset.blockId === blockId);
      if (targetPin) {
        const index = parseInt(targetPin.dataset.hotspotIndex, 10);
        if (window.innerWidth >= 990) {
          this.scrollToDesktopStep(index);
        } else {
          this.scrollToMobileCard(index);
        }
      }
    });
  }
}

if (!customElements.get('fit-hotspot-scroll')) {
  customElements.define('fit-hotspot-scroll', FitHotspotScroll);
}
